import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';

const NutritionalValueCalculator = () => {
  const [formData, setFormData] = useState({
    foodItem: '',
    portionSize: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: ''
  });
  const [results, setResults] = useState(null);
  const [showGraph, setShowGraph] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateNutritionalValue = () => {
    const { portionSize, calories, protein, carbs, fat } = formData;
    
    if (!portionSize || !calories || !protein || !carbs || !fat) {
      alert('Please fill in all required fields!');
      return;
    }

    const totalCalories = parseFloat(calories);
    const proteinCalories = parseFloat(protein) * 4;
    const carbsCalories = parseFloat(carbs) * 4;
    const fatCalories = parseFloat(fat) * 9;

    const proteinPercentage = (proteinCalories / totalCalories) * 100;
    const carbsPercentage = (carbsCalories / totalCalories) * 100;
    const fatPercentage = (fatCalories / totalCalories) * 100;

    setResults({
      proteinPercentage,
      carbsPercentage,
      fatPercentage
    });
    setShowGraph(true);
  };

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function(val) {
        return val.toFixed(1) + '%';
      }
    },
    xaxis: {
      labels: {
        formatter: function(val) {
          return val.toFixed(1) + '%';
        }
      }
    },
    colors: ['#4CAF50', '#2196F3', '#FFC107']
  };

  const chartSeries = results ? [
    {
      name: 'Protein',
      data: [results.proteinPercentage]
    },
    {
      name: 'Carbs',
      data: [results.carbsPercentage]
    },
    {
      name: 'Fat',
      data: [results.fatPercentage]
    }
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-yellow">
      <Helmet>
        <title>Nutritional Value Calculator - Calculate Your Meal's Nutritional Value</title>
        <meta name="description" content="Calculate the nutritional value of your meals including calories, protein, carbs, and fat content. Get detailed analysis and visual representation of your meal's nutritional composition." />
        <meta property="og:title" content="Nutritional Value Calculator" />
        <meta property="og:description" content="Calculate the nutritional value of your meals including calories, protein, carbs, and fat content. Get detailed analysis and visual representation of your meal's nutritional composition." />
        <meta property="og:image" content="https://your-image-url.com/nutrition-calculator.jpg" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-red p-4 rounded-t-lg">
            <h1 className="text-3xl font-bold flex items-center">
              🍽️ Nutritional Value Calculator
            </h1>
          </div>

          <div className="p-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🍽️ Food Item
                  </label>
                  <input
                    type="text"
                    name="foodItem"
                    value={formData.foodItem}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter food item name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    📏 Portion Size (grams)
                  </label>
                  <input
                    type="number"
                    name="portionSize"
                    value={formData.portionSize}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter portion size"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🔥 Calories
                  </label>
                  <input
                    type="number"
                    name="calories"
                    value={formData.calories}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter calories"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🥩 Protein (g)
                  </label>
                  <input
                    type="number"
                    name="protein"
                    value={formData.protein}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter protein content"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🍞 Carbs (g)
                  </label>
                  <input
                    type="number"
                    name="carbs"
                    value={formData.carbs}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter carbohydrate content"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🥤 Fat (g)
                  </label>
                  <input
                    type="number"
                    name="fat"
                    value={formData.fat}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter fat content"
                  />
                </div>
              </div>

              <button
                onClick={calculateNutritionalValue}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 text-green py-3 px-4 rounded-md hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center justify-center">
                  📊 Calculate Nutritional Value
                </span>
              </button>
            </div>

            {showGraph && (
              <div className="mt-8">
                <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                  <div className="flex justify-between items-center mb-8">
                    <h2 className="text-3xl font-bold text-green-600">Nutritional Analysis Results</h2>
                    <div className="px-4 py-2 rounded-full text-sm font-semibold 
                      {results && (
                        results.carbsPercentage >= 45 && results.carbsPercentage <= 65 &&
                        results.proteinPercentage >= 10 && results.proteinPercentage <= 35 &&
                        results.fatPercentage >= 20 && results.fatPercentage <= 35
                      ) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                    ">
                      {results && (
                        results.carbsPercentage >= 45 && results.carbsPercentage <= 65 &&
                        results.proteinPercentage >= 10 && results.proteinPercentage <= 35 &&
                        results.fatPercentage >= 20 && results.fatPercentage <= 35
                      ) ? 'Balanced Meal' : 'Imbalanced Meal'}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-green-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-700 mb-4">Basic Information</h3>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                            <span className="text-green-600">🍽️</span>
                          </div>
                          <div>
                            <p className="font-medium">Food Item</p>
                            <p className="text-sm text-gray-600">{formData.foodItem}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                            <span className="text-gray-600">📏</span>
                          </div>
                          <div>
                            <p className="font-medium">Portion Size</p>
                            <p className="text-sm text-gray-600">{formData.portionSize}g</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-700 mb-4">Caloric Information</h3>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                            <span className="text-red-600">🔥</span>
                          </div>
                          <div>
                            <p className="font-medium">Total Calories</p>
                            <p className="text-2xl font-bold text-green-600">{formData.calories} kcal</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                            <span className="text-yellow-600">⚡</span>
                          </div>
                          <div>
                            <p className="font-medium">Energy per 100g</p>
                            <p className="text-sm text-gray-600">
                              {parseFloat(formData.calories) / parseFloat(formData.portionSize) * 100} kcal/100g
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">Macronutrient Breakdown</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-green-50 p-6 rounded-lg">
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                            <span className="text-green-600">🥩</span>
                          </div>
                          <div>
                            <p className="font-medium">Protein</p>
                            <p className="text-sm text-gray-600">{formData.protein}g</p>
                          </div>
                        </div>
                        <div className="text-3xl font-bold text-green-600 text-center">
                          {results.proteinPercentage.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-600 text-center mt-2">
                          {results.proteinPercentage >= 10 && results.proteinPercentage <= 35 
                            ? '✅ Good protein content' 
                            : '⚠️ Adjust protein intake'}
                        </div>
                      </div>

                      <div className="bg-blue-50 p-6 rounded-lg">
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                            <span className="text-blue-600">🍞</span>
                          </div>
                          <div>
                            <p className="font-medium">Carbohydrates</p>
                            <p className="text-sm text-gray-600">{formData.carbs}g</p>
                          </div>
                        </div>
                        <div className="text-3xl font-bold text-blue-600 text-center">
                          {results.carbsPercentage.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-600 text-center mt-2">
                          {results.carbsPercentage >= 45 && results.carbsPercentage <= 65 
                            ? '✅ Good carbohydrate content' 
                            : '⚠️ Adjust carbohydrate intake'}
                        </div>
                      </div>

                      <div className="bg-yellow-50 p-6 rounded-lg">
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                            <span className="text-yellow-600">🥑</span>
                          </div>
                          <div>
                            <p className="font-medium">Fat</p>
                            <p className="text-sm text-gray-600">{formData.fat}g</p>
                          </div>
                        </div>
                        <div className="text-3xl font-bold text-yellow-600 text-center">
                          {results.fatPercentage.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-600 text-center mt-2">
                          {results.fatPercentage >= 20 && results.fatPercentage <= 35 
                            ? '✅ Good fat content' 
                            : '⚠️ Adjust fat intake'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">Nutritional Insights</h3>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2">✅</span>
                        <div>
                          <p className="font-medium">Protein Content:</p>
                          <p className="text-sm text-gray-600">
                            Your meal contains {results.proteinPercentage.toFixed(1)}% protein, which is essential for muscle repair and growth.
                            {results.proteinPercentage >= 10 && results.proteinPercentage <= 35 
                              ? 'Perfect protein balance!' 
                              : 'Consider adjusting your protein intake.'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <span className="text-blue-600 mr-2">✅</span>
                        <div>
                          <p className="font-medium">Carbohydrate Content:</p>
                          <p className="text-sm text-gray-600">
                            {results.carbsPercentage.toFixed(1)}% of your meal comes from carbohydrates, which provide energy.
                            {results.carbsPercentage >= 45 && results.carbsPercentage <= 65 
                              ? 'Great energy source!' 
                              : 'Consider adjusting your carbohydrate intake.'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <span className="text-yellow-600 mr-2">✅</span>
                        <div>
                          <p className="font-medium">Fat Content:</p>
                          <p className="text-sm text-gray-600">
                            Fat makes up {results.fatPercentage.toFixed(1)}% of your meal, which is important for vitamin absorption.
                            {results.fatPercentage >= 20 && results.fatPercentage <= 35 
                              ? 'Healthy fat balance!' 
                              : 'Consider adjusting your fat intake.'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <span className="text-green-600 mr-2">✅</span>
                        <div>
                          <p className="font-medium">Overall Assessment:</p>
                          <p className="text-sm text-gray-600">
                            {results && (
                              results.carbsPercentage >= 45 && results.carbsPercentage <= 65 &&
                              results.proteinPercentage >= 10 && results.proteinPercentage <= 35 &&
                              results.fatPercentage >= 20 && results.fatPercentage <= 35
                            ) 
                              ? 'Your meal has a balanced macronutrient distribution. Keep it up!' 
                              : 'Your meal could benefit from adjusting one or more macronutrient ratios.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-green-600 mb-4">Nutritional Breakdown</h2>
                  <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    height={350}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-green-600 mb-4">How to Use</h3>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">1.</span>
                <p>Enter your food item name and portion size</p>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">2.</span>
                <p>Input the nutritional values (calories, protein, carbs, fat)</p>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">3.</span>
                <p>Click "Calculate" to see your nutritional breakdown</p>
              </li>
            </ol>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-green-600 mb-4">Benefits</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✅</span>
                <p>Visual representation of your meal's nutritional content</p>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✅</span>
                <p>Percentage breakdown of macronutrients</p>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✅</span>
                <p>Easy to understand nutritional analysis</p>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">FAQs</h2>
          <div className="bg-white rounded-lg shadow-lg">
            <div className="border-b">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-green-600 mb-2">What is a balanced meal?</h3>
                <p>A balanced meal typically contains 45-65% carbs, 10-35% protein, and 20-35% fat.</p>
              </div>
            </div>
            <div className="border-b">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-green-600 mb-2">How often should I use this calculator?</h3>
                <p>Use it whenever you want to analyze your meal's nutritional content or plan your diet.</p>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-green-600 mb-2">Can I use this for multiple meals?</h3>
              <p>Yes, you can use it for individual meals or combine multiple meals to get a daily nutritional overview.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalValueCalculator;
